import { faBars, faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { memo, useEffect, useState } from 'react';
import { Box } from 'src/components/Box';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Stack } from 'src/components/Stack';
import { Tooltip } from 'src/components/Tooltip';
import { useTheme } from 'src/components/muiTheme/utils';
import { Theme } from 'src/components/muiTheme/utils';
import { useMediaQuery } from 'src/components/muiTheme/utils';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { isTrainer } from 'src/models/user/utils';
import { GlobalSearchDialog } from 'src/shared/GlobalSearch/GlobalSearchDialog';

import { ActionsMenu } from './ActionsMenu';
import { DreamcoatNavToggle } from './DreamcoatNavToggle';


export interface TopBarProps {
  toggleOpenNav: () => void;
  hideNavToggle?: boolean;
  isAdmin?: boolean;
  bgcolor: string;
}

export const TopBar: React.FC<TopBarProps> = memo(
  ({ toggleOpenNav, hideNavToggle = false, isAdmin, bgcolor }) => {
    const { session, user, isDarkmode } = useOptionalSession();
    const darkMode = isDarkmode();
    const theme = useTheme();
    const xlDown = useMediaQuery((theme: Theme) =>
      theme.breakpoints.down('xl')
    );
    const [searchOpen, setSearchOpen] = useState(false);

    // Keyboard shortcut: Cmd+K (Mac) or Ctrl+K (Windows)
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
          e.preventDefault();
          if (user && isTrainer(user) && !isAdmin) {
            setSearchOpen(true);
          }
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [user, isAdmin]);

    const adminLogo = darkMode
      ? 'https://res.cloudinary.com/brandpad/image/upload/c_scale,dpr_auto,f_auto,w_2816/v1/13578/colorprism-textlight-h'
      : 'https://res.cloudinary.com/brandpad/image/upload/c_scale,dpr_auto,f_auto,w_2816/v1/13578/colorprism-textdark-h';

    const regularLogo = darkMode ? session?.darkmodeLogoUrl : session?.logoUrl;

    const logoUrl = isAdmin ? adminLogo : regularLogo;

    // We need to take a bgcolor string like "background.level1"
    // and turn it into an actual palette variable by splitting it
    // so we can find the matching channel variable
    const bgcolorSegments = bgcolor?.split('.');
    const bgVar =
      bgcolorSegments.length === 2 &&
      theme.vars.palette[bgcolorSegments[0]] &&
      theme.vars.palette[bgcolorSegments[0]][bgcolorSegments[1] + 'Channel'];

    return (
      <>
        <Box
          data-app-top-bar="true"
          sx={{
            position: 'sticky',
            top: '0px',
            height: '56px',
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: (theme) =>
              bgVar ||
              `rgba(${theme.vars.palette.background.defaultChannel} / 0.7)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            zIndex: theme.zIndex?.appBar,
            backdropFilter: 'blur(10px)',
            transition: 'background-color 0.5s',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {!hideNavToggle && (
              <IconButton onClick={toggleOpenNav} sx={{ mr: 2 }}>
                <Icon
                  color="action"
                  size="medium"
                  icon={faBars}
                />
              </IconButton>
            )}
            {xlDown ? (
              <Box
                component="img"
                src={logoUrl}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '38px',
                  height: 'auto',
                  width: '100%',
                  objectFit: 'contain',
                  objectPosition: 'left center',
                  my: 1,
                }}
              />
            ) : (
              <Box />
            )}
          </Box>
          <Stack direction="row" alignItems="center" gap={1}>
            {!isAdmin && <DreamcoatNavToggle />}
            {user && session && isTrainer(user) && !isAdmin && (
              <Tooltip title="Search (⌘K)">
                <IconButton onClick={() => setSearchOpen(true)}>
                  <Icon
                    color="action"
                    size="medium"
                    icon={faMagnifyingGlass}
                  />
                </IconButton>
              </Tooltip>
            )}
            {!isAdmin && <ActionsMenu />}
          </Stack>
        </Box>
        {user && session && isTrainer(user) && !isAdmin && (
          <GlobalSearchDialog
            open={searchOpen}
            onClose={() => setSearchOpen(false)}
          />
        )}
      </>
    );
  }
);

TopBar.displayName = 'TopBar';
